import { PaymentService } from 'src/app/angularJS/services/payments/payments.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { PanelMenuController } from './panel-menu.controller';


/**
 * A Controller using the {@link actora.services.PaymentService} to handle the retrieval and display of payment transaction history details.
 */
export class PaymentListController {

  public static $inject = [
    '$rootScope',
    'customer',
    'paymentConstants',
    'paymentService',
    '$mdPanel',
    '$state',
    'QueryBuilder'
  ];

  public paymentHistoryList: Array<InterACT.Interfaces.Payments.PaymentRequest>;
  public paginatorData: InterACT.Interfaces.IPaginationData = { currentPage: 1, perPageCount: 15 };
  public paymentTokens: Array<InterACT.Interfaces.Payments.PaymentToken>;
  public totalCost: number;
  public paymentStatus: {};
  public selectedPaymentToken: {};
  public paymentDateRange: InterACT.Interfaces.IPaymentDateRange = {};
  public zeroPaymentHistory: boolean;
  private sortedColumnName: string;
  private sortDirection: string;
  private currentFilter: string;
  constructor(
    private $rootScope,
    private customer: InterACT.Interfaces.Customers.Customer,
    private paymentConstants,
    private paymentService: PaymentService,
    private $mdPanel,
    private $state: ng.ui.IStateService,
    private queryBuilder: QueryBuilder) {
    this.customer = customer;
    this.paymentStatus = this.paymentConstants.PAYMENT_STATUS;
    this.activate();
  }
  /**
   * A method that makes a service call to retrieve payment data sorted by a specific columnName and direction
   *
   * @param columnName the name of the column to sort the operator data on
   * @param sortDirection the direction of the sort to be applied. i.e. asc or desc
   *
   * @returns A promise that resolves with a list of sorted operators
   */
  public sortDataByColumnName = (columnName, sortDirection) => {
    if (!columnName || !sortDirection) {
      return;
    }
    this.sortedColumnName = columnName;
    this.sortDirection = sortDirection;
    return this.fetchPaymentHistory(1, 15);
  }

  /**
   * This function is used to navigate to ticketHistory page.
   * @param paymentHistory paymentHistory for which ticket history needs to be populated
   */
  public navigateToTicketHistory(paymentHistory?: InterACT.Interfaces.Payments.PaymentRequest) {
    if (!_.isUndefined(paymentHistory)) {
      let paymentHistoryId = paymentHistory.id;
      if (_.isEmpty(paymentHistory.items)) {
        paymentHistoryId = '';
      }
      this.$state.go('customer.payments.ticketHistory', { paymentHistoryId: paymentHistoryId });
    }
  }

  /**
   * A method which is used to open the payment date range.
   *
   * @param event angular event
   */
  public showMenu(event: any) {
    const position = this.$mdPanel.newPanelPosition()
      .relativeTo('.menu-open-button')
      .addPanelPosition(this.$mdPanel.xPosition.ALIGN_START, this.$mdPanel.yPosition.BELOW);

    const config = {
      attachTo: angular.element(document.body),
      controller: PanelMenuController,
      controllerAs: 'vm',
      templateUrl: 'app/angularJS/features/payments/list/paymentDateRange.html',
      panelClass: 'panel-menu',
      position: position,
      locals: {
        onFetch: this.fetchPaymentHistory,
        paymentDateRange: this.paymentDateRange
      },
      openFrom: event,
      clickOutsideToClose: true,
      escapeToClose: true,
      focusOnOpen: false,
      zIndex: 2
    };
    this.$mdPanel.open(config);
  }

  /**
   * Function to retrieve a given page of payment history from the customer service.
   *
   * @param page The number of the page of data to be retrieved
   * @param perPage The number of items per page to be retrieved
   *
   * @returns The response object returned from a httpPromise for payment history.
   */
  public fetchPaymentHistory = (page?: number, perPage?: number) => {
    this.setPageData(page, perPage);
    this.$rootScope.showSpinner = true;

    return this.paymentService.getPaymentHistory(
      this.paginatorData.currentPage,
      this.paginatorData.perPageCount,
      this.constructQParam()).then((paymentList) => {
        this.$rootScope.showSpinner = false;
        if (paymentList.meta.totalCount > 0) {
          this.paymentHistoryList = paymentList.paymentRequests;
          this.paginatorData.totalCount = paymentList.meta.totalCount;
          this.getPaymentSummary();
          this.zeroPaymentHistory = false;
        } else {
          this.zeroPaymentHistory = true;
        }
      });
  }

  /**
   * A method which is used to get total cost of all payments for selected range.
   *
   * @returns sum of all payments for selected date range on selected card
   */
  private getPaymentSummary() {
    this.$rootScope.showSpinner = true;
    const aggregations = 'amount';
    return this.paymentService.getPaymentSummary(
      undefined, undefined,
      this.constructQParam(),
      'amount')
      .then((paymentSummary) => {
        this.totalCost = paymentSummary.sum;
        this.$rootScope.showSpinner = false;
      });
  }

  /**
   * A method which is used initialize controller
   */
  private activate() {
    this.setCardDetails();
    this.sortedColumnName = undefined;
    this.sortDirection = undefined;
    this.paymentHistoryList = [];
    this.zeroPaymentHistory = false;
    this.selectedPaymentToken = 'All';
    this.paymentDateRange = {
      selectedPaymentDateRange: 'PAYMENT_DATE.LAST_30_DAYS',
      startDate: moment().subtract(29, 'days').toDate(),
      endDate: new Date(),
      isCustomRangeActive: false
    };
  }

  /**
   * Call payment token api to get already registered Payment Cards.
   */
  private setCardDetails() {
    const param = 'customer.link=/customers/' + this.customer.id;
    this.paymentService.getPaymentTokens(undefined, undefined, param).then((paymentTokens) => {
      this.paymentTokens = paymentTokens;
    });
  }

  /**
    * This function is used to set the paginator data for requests
    *
    * @param page the new current page
    * @param perPage the new per page count
    */
  private setPageData(page?: number, perPage?: number) {
    this.paginatorData.currentPage = angular.isNumber(page) ? page : 1;
    this.paginatorData.perPageCount = angular.isNumber(perPage) ? perPage : this.paginatorData.perPageCount;
  }

  /**
    * A method that builds and returns a q parameter to query the payment services with
    *
    * @param aggregations for query result aggregations
    *
    * @returns a q parameter to query the payment service with
    */
  private constructQParam() {
    const query = this.queryBuilder.newParam().customerLinkIs(this.customer.id).and().not().statusIs('CANCELLED');
    if (this.selectedPaymentToken !== 'All') {
      query.and().paymentTokenLinkIs(this.selectedPaymentToken['id']);
    }
    if (!_.isUndefined(this.paymentDateRange.startDate)) {
      query.and().isGreaterOrEqualTo('date', moment(this.paymentDateRange.startDate).format('YYYY-MM-DD') + 'T00:00:00Z');
    }
    if (!_.isUndefined(this.paymentDateRange.endDate)) {
      query.and().isLessThanOrEqualTo('date', moment(this.paymentDateRange.endDate).format('YYYY-MM-DD') + 'T23:59:59Z');
    }
    if (this.sortedColumnName && this.sortDirection) {
      query.sortBy([
        { columnName: this.sortedColumnName, direction: this.sortDirection }
      ]);
    } else {
      query.sortBy([
        { columnName: 'date', direction: 'desc' }
      ]);
    }
    return query.getParam();
  }
}
